import { createSlice } from '@reduxjs/toolkit';

const initialState = {
	data: [],
	loading: false,
	error: null,
	limit: 10,
	offset: 0,
	totalCount: 0,
	detail: null,
	issuedForDownload: [],
	// 발급이 끝날 때마다 올라가는 세대. 사이드바의 보유 라이선스가 이 값을 보고 다시 읽는다.
	issuedGeneration: 0,
	// 발급 요청이 진행 중인가. 발급 버튼이 이 값을 보고 잠긴다. `loading` 은 목록·상세
	// 조회도 함께 쓰므로 그것으로는 발급 중인지 알 수 없다.
	issuing: false,
};

export const licenseSlice = createSlice({
	name: 'license',
	initialState,
	reducers: {
		setReset: (state) => {
			Object.assign(state, initialState);
		},
		setLoading: (state, action) => {
			state.loading = action.payload;
		},

		// 라이선스 조회
		fetchLicenseRequest: (state, action) => {
			state.loading = true;
			state.limit = action.payload.limit;
			state.offset = action.payload.offset;
		},
		fetchLicenseSuccess: (state, action) => {
			state.data = action.payload.data;
			state.totalCount = action.payload.totalCount;
			state.error = null;
		},
		fetchLicenseFailure: (state) => {
			setReset();
		},

		// 라이선스 등록 (단일 발급)
		fetchAddLicenseRequest: (state) => {
			state.loading = true;
		},
		fetchAddLicenseSuccess: (state, action) => {
			state.loading = false;
			state.data.push(action.payload);
			state.error = null;
		},
		fetchAddLicenseFailure: (state) => {
			Object.assign(state, initialState);
		},

		// WEBCOM-5519: 라이선스 묶음 발급 (TFD+TFO)
		// 단일 트랜잭션으로 두 라이선스 동시 발급 → ZIP 으로 묶어 다운로드
		fetchAddBundleLicenseRequest: (state) => {
			state.loading = true;
			state.issuing = true;
		},
		// 방금 발급한 것을 담아 둔다. 확인 창이 이 값으로 열린다 — 발급 모달은 성공하면
		// 스스로 닫혀서, 무엇을 받아야 하는지 그 안에 둘 수 없다.
		fetchAddBundleLicenseSuccess: (state, action) => {
			state.loading = false;
			state.issuing = false;
			state.error = null;
			state.issuedForDownload = action.payload ?? [];
			// 발급이 끝났다는 신호. 발급 건수나 목록이 아니라 <b>세대</b>라서, 같은 건을
			// 다시 받아도 화면이 다시 읽는다. 사이드바의 보유 라이선스가 이 값을 본다 -
			// 없으면 발급 직후에도 옛 수치가 남아 새로고침해야 바뀐다.
			state.issuedGeneration = (state.issuedGeneration ?? 0) + 1;
		},
		clearIssuedForDownload: (state) => {
			state.issuedForDownload = [];
		},
		// 일부만 발급되고 실패했거나, 응답이 끊겨 나갔는지 모를 때. 나갔을 수 있으므로 세대를
		// 올린다 — 보유 수치도 다시 읽어야 하고, 발급 폼은 이것을 보고 같은 내용으로만 다시
		// 보내게 한다(내용을 바꾸면 새 번호라 나간 것까지 한 번 더 나간다).
		notePartiallyIssued: (state) => {
			state.issuedGeneration = (state.issuedGeneration ?? 0) + 1;
		},
		fetchAddBundleLicenseFailure: (state, action) => {
			state.loading = false;
			state.issuing = false;
			state.error = action.payload;
		},

		// 특정 라이선스 조회
		fetchLicenseDetailRequest: (state) => {
			// Object.assign(state, initialState);
			state.loading = true;
		},
		fetchLicenseDetailSuccess: (state, action) => {
			state.loading = false;
			state.detail = action.payload;
		},
		fetchLicenseDetailFailure: (state, action) => {
			state.loading = false;
			// 문구는 화면이 현재 언어로 채운다. 여기서 정하면 언어를 바꿔도 남는다.
			state.error = action.payload || null;
			state.detail = null;
		},

		// 라이선스 수정
		fetchUpdateLicenseRequest: (state, action) => {
			state.loading = true;
			state.licenseId = action.payload.licenseId;
		},
		fetchUpdateLicenseSuccess: (state, action) => {
			const updatedLicense = action.payload;
			// 현재 데이터 배열에서 수정된 라이선스를 찾아서 업데이트
			state.data = state.data.map(license => {
				if (license.id === updatedLicense.id) {
					return updatedLicense;
				}
				return license;
			});
			state.loading = false;
			state.error = null;
		},
		fetchUpdateLicenseFailure: (state) => {
			// detail 유지 — 사용자가 같은 페이지에서 다시 수정 시도 가능
			state.loading = false;
		},

		// 라이선스 검색
		fetchSearchLicenseRequest: (state, action) => {
			state.loading = true;
			state.limit = action.payload.limit;
			state.offset = action.payload.offset;
		},

		fetchSearchLicenseSuccess: (state, action) => {
			state.data = action.payload.data;
			state.totalCount = action.payload.totalCount;
			state.error = null;
		},

		fetchSearchLiceneseFailure: () => {
			setReset();
		},

		// 파일 다운로드. 포탈이 보관한 파일을 그대로 받는다 - 재발급이 아니다.
		fetchDownloadLicenseRequest: (state) => {
			state.loading = true;
		},
	},
});

export const {
	setReset,
	fetchDownloadLicenseRequest,
	clearIssuedForDownload,
	notePartiallyIssued,
	setLoading,
	fetchLicenseRequest,
	fetchLicenseSuccess,
	fetchLicenseFailure,
	fetchAddLicenseRequest,
	fetchAddLicenseSuccess,
	fetchAddLicenseFailure,
	fetchAddBundleLicenseRequest,
	fetchAddBundleLicenseSuccess,
	fetchAddBundleLicenseFailure,
	fetchLicenseDetailRequest,
	fetchLicenseDetailSuccess,
	fetchLicenseDetailFailure,
	fetchUpdateLicenseRequest,
	fetchUpdateLicenseSuccess,
	fetchUpdateLicenseFailure,
	fetchSearchLicenseRequest,
	fetchSearchLicenseSuccess,
	fetchSearchLiceneseFailure
} = licenseSlice.actions;

export default licenseSlice.reducer;